<script setup>
import {
    NCard, NSpace, NH1, NH2, NH3, NP, NForm, NFormItem,
    NInput, NButton, NSelect, NGradientText,
    NGrid, NGridItem, NAlert
} from 'naive-ui';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    Mail, Phone, MapPin, Clock, MessageCircle,
    Send, User, HelpCircle
} from 'lucide-vue-next';
import { dt } from '@/utils/i18n';

const { t, locale } = useI18n();

// Contact form data
const formData = ref({
    name: '',
    email: '',
    phone: '',
    subject: '',
    category: null,
    message: ''
});

const loading = ref(false);

// Contact categories
const categories = [
    { label: 'contact.form.categories.general', value: 'general' },
    { label: 'contact.form.categories.technical', value: 'technical' },
    { label: 'contact.form.categories.lawyer', value: 'lawyer' },
    { label: 'contact.form.categories.billing', value: 'billing' },
    { label: 'contact.form.categories.partnership', value: 'partnership' }
];

// Contact information - using generic business email and support hours
const contactInfo = [
    {
        icon: Mail,
        title: 'contact.info.email.title',
        details: ['contact.info.email.general', 'contact.info.email.support'],
        color: '#5b86e5'
    },
    {
        icon: MapPin,
        title: 'contact.info.address.title',
        details: ['contact.info.address.line1', 'contact.info.address.line2'],
        color: '#ef473a'
    },
    {
        icon: Clock,
        title: 'contact.info.hours.title',
        details: ['contact.info.hours.weekdays', 'contact.info.hours.weekends'],
        color: '#f7b733'
    }
];

// Submit form - this would send to your backend API endpoint
const submitForm = async () => {
    loading.value = true;
    try {
        // TODO: Replace with actual API endpoint
        // const response = await fetch('/api/contact', {
        //     method: 'POST',
        //     headers: { 'Content-Type': 'application/json' },
        //     body: JSON.stringify(formData.value)
        // });

        // Simulate API call for now
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Show success message
        window.$message?.success(dt('contact.form.success'));

        // Reset form
        Object.keys(formData.value).forEach(key => {
            formData.value[key] = '';
        });
        formData.value.category = null;

    } catch (error) {
        window.$message?.error(dt('contact.form.error'));
    } finally {
        loading.value = false;
    }
};
</script>

<template>
    <div class="contact-container">
        <!-- Hero Section -->
        <div class="hero-section">
            <NH1 class="hero-title">
                <NGradientText :gradient="{ from: '#36d1dc', to: '#5b86e5', deg: 45 }">
                    {{ dt('contact.heroTitle') }}
                </NGradientText>
            </NH1>
            <NP class="hero-subtitle">{{ dt('contact.heroSubtitle') }}</NP>
        </div>

        <!-- Security Notice -->
        <NAlert type="info" class="security-alert">
            <template #icon>
                <MessageCircle />
            </template>
            <strong>Privacy & Security:</strong> 
            For your security, please do not include sensitive personal information (such as case details, financial information, or confidential data) in this contact form. If you need to discuss sensitive matters, please wait for our team to establish a secure communication channel with you.
        </NAlert>

        <!-- Contact Information -->
        <div class="contact-info-section">
            <NH2 class="section-title">{{ dt('contact.infoTitle') }}</NH2>
            <NGrid :cols="3" :x-gap="20" :y-gap="20" responsive="screen">
                <NGridItem v-for="(info, index) in contactInfo" :key="index" :span="24 / 3">
                    <div class="info-card">
                        <div class="info-icon" :style="{ backgroundColor: info.color }">
                            <component :is="info.icon" size="24" color="white" />
                        </div>
                        <div class="info-content">
                            <NH3 class="info-title">{{ dt(info.title) }}</NH3>
                            <div class="info-details">
                                <div v-for="detail in info.details" :key="detail" class="info-detail">
                                    {{ dt(detail) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </NGridItem>
            </NGrid>
        </div>

        <!-- Contact Form -->
        <div class="form-section">
            <NGrid :cols="2" :x-gap="40" responsive="screen" :collapsed="['xs', 's']">
                <!-- Form -->
                <NGridItem>
                    <NCard class="form-card">
                        <NH2 class="form-title">{{ dt('contact.form.title') }}</NH2>
                        <NP class="form-subtitle">{{ dt('contact.form.subtitle') }}</NP>

                        <NForm :model="formData" label-placement="top">
                            <NFormItem :label="dt('contact.form.name')" required>
                                <NInput v-model:value="formData.name" :placeholder="dt('contact.form.namePlaceholder')"
                                    size="large">
                                    <template #prefix>
                                        <User size="18" />
                                    </template>
                                </NInput>
                            </NFormItem>

                            <NFormItem :label="dt('contact.form.email')" required>
                                <NInput v-model:value="formData.email"
                                    :placeholder="dt('contact.form.emailPlaceholder')" size="large" type="email">
                                    <template #prefix>
                                        <Mail size="18" />
                                    </template>
                                </NInput>
                            </NFormItem>

                            <NFormItem :label="dt('contact.form.phone')">
                                <NInput v-model:value="formData.phone"
                                    :placeholder="dt('contact.form.phonePlaceholder')" size="large">
                                    <template #prefix>
                                        <Phone size="18" />
                                    </template>
                                </NInput>
                            </NFormItem>

                            <NFormItem :label="dt('contact.form.category')" required>
                                <NSelect v-model:value="formData.category" :options="categories.map(cat => ({
                                    label: dt(cat.label),
                                    value: cat.value
                                }))" :placeholder="dt('contact.form.categoryPlaceholder')" size="large" />
                            </NFormItem>

                            <NFormItem :label="dt('contact.form.subject')" required>
                                <NInput v-model:value="formData.subject"
                                    :placeholder="dt('contact.form.subjectPlaceholder')" size="large">
                                    <template #prefix>
                                        <HelpCircle size="18" />
                                    </template>
                                </NInput>
                            </NFormItem>

                            <NFormItem :label="dt('contact.form.message')" required>
                                <NInput v-model:value="formData.message" type="textarea"
                                    :placeholder="dt('contact.form.messagePlaceholder')" :rows="5" size="large" />
                            </NFormItem>

                            <NFormItem>
                                <NButton type="primary" size="large" @click="submitForm" :loading="loading"
                                    class="submit-button">
                                    <template #icon>
                                        <Send size="18" />
                                    </template>
                                    {{ dt('contact.form.submit') }}
                                </NButton>
                            </NFormItem>
                        </NForm>
                    </NCard>
                </NGridItem>

                <!-- Quick Contact & FAQs -->
                <NGridItem>
                    <div class="sidebar">
                        <!-- Quick Contact -->
                        <NCard class="quick-contact-card">
                            <NH3 class="quick-contact-title">{{ dt('contact.quickContact.title') }}</NH3>
                            <div class="quick-contact-options">
                                <div class="quick-option">
                                    <div class="quick-icon email">
                                        <Mail size="20" />
                                    </div>
                                    <div class="quick-content">
                                        <div class="quick-label">{{ dt('contact.quickContact.email') }}</div>
                                        <div class="quick-value">hello@lawdosti.com</div>
                                    </div>
                                </div>

                                <div class="quick-option">
                                    <div class="quick-icon chat">
                                        <MessageCircle size="20" />
                                    </div>
                                    <div class="quick-content">
                                        <div class="quick-label">{{ dt('contact.quickContact.chat') }}</div>
                                        <div class="quick-value">{{ dt('contact.quickContact.chatDesc') }}</div>
                                    </div>
                                </div>
                            </div>
                        </NCard>

                        <!-- Common Questions -->
                        <NCard class="faq-preview-card">
                            <NH3 class="faq-title">{{ dt('contact.commonQuestions.title') }}</NH3>
                            <div class="faq-list">
                                <div v-for="i in 4" :key="i" class="faq-item">
                                    <div class="faq-question">
                                        {{ dt(`contact.commonQuestions.q${i}.question`) }}
                                    </div>
                                    <div class="faq-answer">
                                        {{ dt(`contact.commonQuestions.q${i}.answer`) }}
                                    </div>
                                </div>
                            </div>
                            <NButton text type="primary" class="view-all-faq">
                                {{ dt('contact.commonQuestions.viewAll') }}
                            </NButton>
                        </NCard>
                    </div>
                </NGridItem>
            </NGrid>
        </div>

        <!-- Response Time Alert -->
        <NAlert type="info" class="response-alert">
            <template #icon>
                <Clock />
            </template>
            {{ dt('contact.responseTime') }}
        </NAlert>
    </div>
</template>

<style scoped>
.contact-container {
    padding: 16px;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    color: #333;
    overflow-x: hidden;
    box-sizing: border-box;
}

.hero-section {
    text-align: center;
    padding: 40px 0;
}

.hero-subtitle {
    font-size: 1.2rem;
    margin-top: 12px;
    color: #555;
}

.security-alert {
    margin: 24px 0;
    border-radius: 12px;
}

.section-title {
    text-align: center;
    margin-bottom: 30px;
    font-size: 1.5rem;
    position: relative;
}

.section-title::after {
    content: "";
    display: block;
    width: 50px;
    height: 3px;
    background: linear-gradient(135deg, #36d1dc, #5b86e5);
    margin: 10px auto 0;
    border-radius: 2px;
}

.contact-info-section {
    margin: 60px 0;
}

.info-card {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 24px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s ease;
    height: 100%;
}

.info-card:hover {
    transform: translateY(-3px);
}

.info-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.info-content {
    flex: 1;
}

.info-title {
    margin-bottom: 8px;
    color: #333;
    font-size: 1.1rem;
}

.info-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.info-detail {
    color: #666;
    font-size: 0.95rem;
}

.form-section {
    margin: 60px 0;
}

.form-card {
    border-radius: 16px;
    overflow: hidden;
}

.form-title {
    margin-bottom: 8px;
    color: #333;
}

.form-subtitle {
    margin-bottom: 24px;
    color: #666;
}

.submit-button {
    width: 100%;
    background: linear-gradient(135deg, #36d1dc, #5b86e5);
    border: none;
    height: 48px;
}

.sidebar {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.quick-contact-card,
.faq-preview-card {
    border-radius: 16px;
    overflow: hidden;
}

.quick-contact-title,
.faq-title {
    margin-bottom: 20px;
    color: #333;
}

.quick-contact-options {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.quick-option {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
    background: #f8f9fa;
    transition: background-color 0.3s ease;
}

.quick-option:hover {
    background: #e9ecef;
}

.quick-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.quick-icon.email {
    background: #5b86e5;
}

.quick-icon.chat {
    background: #ef473a;
}

.quick-content {
    flex: 1;
}

.quick-label {
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 2px;
}

.quick-value {
    font-weight: 500;
    color: #333;
}

.faq-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
}

.faq-item {
    padding: 16px;
    border-radius: 8px;
    background: #f8f9fa;
}

.faq-question {
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
    font-size: 0.95rem;
}

.faq-answer {
    color: #666;
    font-size: 0.9rem;
    line-height: 1.5;
}

.view-all-faq {
    width: 100%;
    text-align: center;
}

.response-alert {
    margin: 40px 0;
    border-radius: 12px;
}

@media screen and (max-width: 768px) {
    .contact-info-section {
        margin: 40px 0;
    }

    .info-card {
        flex-direction: column;
        text-align: center;
    }

    .sidebar {
        margin-top: 30px;
    }

    .quick-option {
        flex-direction: column;
        text-align: center;
        padding: 16px;
    }

    .quick-content {
        margin-top: 8px;
    }
}
</style>